import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import { useTranslation } from 'react-i18next';
import CustomDatePicker from "../../components/forms/DatePicker";
import reservationsWS from "../../services/wsReservations";
import { toPersianDigits } from "../../utils/persianNumbers";

const REFRESH_INTERVAL = 15000; // 30 seconds - adjust as needed
// const API_BASE_URL = process.env.REACT_APP_BASE_DOMAIN;
const API_BASE_URL = "";

const Room = ({ onEventCreated }) => {
  const { t, i18n } = useTranslation();
  const isPersian = i18n.language === 'fa';
  const currentDir = i18n.dir();
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [clock, setClock] = useState("");
  const [formData, setFormData] = useState({
    resource_id: roomId,
    title: t('default_event_name'),
    start: moment().format("YYYY-MM-DDTHH:mm:ss"),
    end: moment().add(60, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
    description: "",
  });
  const [error, setError] = useState(null);
  const [isOccupied, setIsOccupied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [roomDetails, setRoomDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeHolds, setActiveHolds] = useState([]);
  const holdsRef = useRef({});
  const unsubRef = useRef(null);
  const lastInteractionRef = useRef(new Date());
  const todayRef = useRef(new Date());

  const fetchRoomDetails = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/resources/${roomId}/`
      );
      setRoomDetails(response.data);
    } catch (error) {
      console.error("Error fetching room details:", error);
    }
  }, [roomId]);

  const processReservations = (events) => {
    const now = moment();
    const nowTime = now.format("HH:mm:ss"); // Extract only time

    const occupiedEvent = events.find((event) => {
      const eventStart = moment(event.occurrence_start || event.start).format("HH:mm:ss");
      const eventEnd = moment(event.occurrence_end || event.end).format("HH:mm:ss");
      return nowTime >= eventStart && nowTime < eventEnd;
    });

    setIsOccupied(!!occupiedEvent);
    setCurrentEvent(occupiedEvent || null);
    setUpcomingEvents(
      events
        .filter((event) =>
          moment(event.occurrence_start || event.start)
            .set({ year: moment().year(), month: moment().month(), date: moment().date() })
            .isAfter(now)
        )
        .sort(
          (a, b) =>
            moment(a.occurrence_start || a.start).valueOf() -
            moment(b.occurrence_start || b.start).valueOf()
        )
    );
  };

  useEffect(() => {
    const events = ["click", "keydown", "scroll", "touchstart"];
    const updateInteraction = () => { lastInteractionRef.current = new Date(); };

    events.forEach((eventName) => window.addEventListener(eventName, updateInteraction));

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, updateInteraction));
    };
  }, []);

  // Update clock with Persian numerals when language changes
  useEffect(() => {
    if (clock) {
      const baseTime = clock.replace(/[۰-۹]/g, (digit) => {
        const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        return persianDigits.indexOf(digit).toString();
      });
      setClock(isPersian ? toPersianDigits(baseTime) : baseTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersian]);

  useEffect(() => {
    setLoading(true);
    fetchRoomDetails();

    // Subscribe to reservations for today scoped to this room
    const todayStr = moment().format("YYYY-MM-DD");

    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    unsubRef.current = reservationsWS.addListener((list, meta) => {
      // Expect list for this date and resource
      if (!meta) return;
      if (meta.date === todayStr && String(meta.resource_id) === String(roomId)) {
        processReservations(list);
        setLoading(false);
      }
    });
    reservationsWS.subscribe(todayStr, Number(roomId));

    // Listen for ephemeral hold events for this room/date
    const unsubHold = reservationsWS.addHoldListener((payload) => {
      if (!payload) return;
      const { action, date, resource_id, start_minute, end_minute, user_name, ts, hold_id } = payload;
      if (String(resource_id) !== String(roomId)) return;
      if (date !== todayStr) return;
      const key = `${hold_id || ''}-${resource_id}`;
      const next = { ...holdsRef.current };
      if (action === 'hold_end') {
        delete next[key];
      } else {
        next[key] = {
          resource_id: Number(resource_id),
          start_minute: Number(start_minute) || 0,
          end_minute: Number(end_minute) || Number(start_minute) || 0,
          user_name: user_name || 'Someone',
          ts: ts || new Date().toISOString(),
        };
      }
      holdsRef.current = next;
      setActiveHolds(Object.values(next));
    });

    const unsubHoldSnap = reservationsWS.addHoldSnapshotListener((list, meta) => {
      if (!meta || meta.date !== todayStr) return;
      const filtered = list.filter(p => String(p.resource_id) === String(roomId));
      const next = {};
      filtered.forEach((p, idx) => {
        const key = `${p.hold_id || idx}-${p.resource_id}`;
        next[key] = {
          resource_id: Number(p.resource_id),
          start_minute: Number(p.start_minute) || 0,
          end_minute: Number(p.end_minute) || Number(p.start_minute) || 0,
          user_name: p.user_name || 'Someone',
          ts: p.ts || new Date().toISOString(),
        };
      });
      holdsRef.current = next;
      setActiveHolds(Object.values(next));
    });

    // Periodically prune stale holds (e.g., if user leaves without sending hold_end)
    const pruneInterval = setInterval(() => {
      const now = Date.now();
      const next = { ...holdsRef.current };
      Object.entries(next).forEach(([k, v]) => {
        const t = Date.parse(v.ts || 0);
        if (!t || now - t > 15000) { // 15s TTL
          delete next[k];
        }
      });
      if (Object.keys(next).length !== Object.keys(holdsRef.current).length) {
        holdsRef.current = next;
        setActiveHolds(Object.values(next));
      }
    }, 5000);

    const updateClock = setInterval(() => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const timeString = `${hours}:${minutes}:${seconds}`;
      setClock(isPersian ? toPersianDigits(timeString) : timeString);
      if (now.getDate() !== todayRef.current.getDate()) {
        todayRef.current = now;
        window.location.reload();
      }
    }, 1000);

    // Update form start and endtime every minute
    const updateFormTimeInterval = setInterval(() => {
      if (new Date().getTime() - lastInteractionRef.current.getTime() > 60 * 1000) {
        setFormData((prev) => ({
          ...prev,
          start: moment().format("YYYY-MM-DDTHH:mm:ss"),
          end: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm:ss"),
        }));
        setError(null);
      }
    }, REFRESH_INTERVAL);

    return () => {
      clearInterval(updateClock);
      clearInterval(updateFormTimeInterval);
      clearInterval(pruneInterval);
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      if (typeof unsubHold === 'function') unsubHold();
      if (typeof unsubHoldSnap === 'function') unsubHoldSnap();
    };
  }, [roomId, fetchRoomDetails, isPersian]);

  useEffect(() => {
    // Prevent going back
    window.history.pushState(null, "", window.location.pathname);
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, "", window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Clear holds when room changes (to avoid bleed from previous room)
  useEffect(() => {
    holdsRef.current = {};
    setActiveHolds([]);
  }, [roomId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "start" || name === "end") {
      setFormData((prev) => ({ ...prev, [name]: moment(value).format("YYYY-MM-DDTHH:mm:ss") }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const triggerImmediateRefresh = () => {
    // Ask server for a fresh snapshot immediately
    reservationsWS.updateSubscription(moment().format("YYYY-MM-DD"), Number(roomId));
    reservationsWS.refresh();
  };

  const handleReserve = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/reservations/`, {
        ...formData,
        start: moment(formData.start).format("YYYY-MM-DDTHH:mm:ss"),
        end: moment(formData.end).format("YYYY-MM-DDTHH:mm:ss"),
      });
      setShowModal(false);
      setShowSuccessModal(true);
      triggerImmediateRefresh();
    } catch (error) {
      console.error("Error creating reservation:", error);
      setError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error.response?.data?.non_field_errors ||
          t('room_error_reserve')
      );
    }
  };

  const handleCancel = async () => {
    if (!currentEvent) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/reservations/${currentEvent.id}/`);
      triggerImmediateRefresh();
    } catch (error) {
      console.error("Error canceling event:", error);
      setError(t('room_error_cancel'));
    }
  };

  const handleReserveAfterCurrent = () => {
    if (!currentEvent) return;
    const startMoment = moment(currentEvent.occurrence_end || currentEvent.end);
    const endMoment = moment(startMoment).add(60, "minutes");
    setFormData((prev) => ({
      ...prev,
      start: startMoment.format("YYYY-MM-DDTHH:mm:ss"),
      end: endMoment.format("YYYY-MM-DDTHH:mm:ss"),
    }));
    setError(null);
    setShowModal(true);
  };

  const handleExit = () => {
    navigate("/rooms");
  };

  if (loading) return <p>{t('loading')}</p>;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        background: "var(--surface-alt)",
      }}
    >
      <button onClick={handleExit} className="btn btn-secondary logout-btn" style={{ opacity: 0 }}>
        {t('room_exit')}
      </button>

      {/* Left Section */}
      <div style={{ flex: 1, padding: "30px", borderRight: "1px solid #ddd" }}>
        <div style={{ display: "flex" }}>
          <h1
            style={{
              textAlign: "center",
              color: "var(--text)",
              borderRadius: "10px",
              marginBottom: "20px",
              maxWidth: "fit-content",
              padding: "5px",
              margin: "5px auto",
            }}
          >
            {roomDetails?.name || roomId}
          </h1>
          <h1
            style={{
              textAlign: "center",
              color: "var(--text)",
              backgroundColor: roomDetails?.color || "var(--surface)",
              borderRadius: "10px",
              marginBottom: "20px",
              maxWidth: "fit-content",
              maxHeight: "fit-content",
              padding: "5px",
              margin: " auto",
            }}
          >
            {clock}
          </h1>
        </div>
        {isOccupied && currentEvent ? (
          <div className="card" style={{ textAlign: "center" }}>
            <h2 style={{ color: "#d32f2f", marginBottom: "10px" }}>
              {t('room_occupied')}
            </h2>
            <p>
              <strong>{t('room_title_label')}</strong> {currentEvent.title}
            </p>
            <p>
              <strong>{t('room_start_label')}</strong>{" "}
              {moment(currentEvent.occurrence_start || currentEvent.start).format("LLLL")}
            </p>
            <p>
              <strong>{t('room_end_label')}</strong> {moment(currentEvent.occurrence_end || currentEvent.end).format("LLLL")}
            </p>
            <p>
              <strong>{t('room_reserved_by')}</strong> {currentEvent.user_name}
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "center",
                marginTop: "15px",
              }}
            >
              {currentEvent.can_delete && (
                <button onClick={handleCancel} className="btn btn-danger">
                  {t('room_end_meeting')}
                </button>
              )}
              <button onClick={handleReserveAfterCurrent} className="btn btn-primary">
                {t('room_reserve_after')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleReserve} className="card" style={{ textAlign: "center" }}>
            {activeHolds.length > 0 && (
              <div style={{
                marginBottom: '12px',
                padding: '8px 12px',
                background: 'color-mix(in oklab, var(--color-warning) 14%, var(--surface))',
                border: '1px solid',
                borderColor: 'color-mix(in oklab, var(--color-warning) 40%, var(--border))',
                color: 'var(--text)',
                borderRadius: '6px',
                textAlign: 'center',
                fontSize: '14px',
              }}>
                {t('room_another_user_selecting')}
              </div>
            )}
            <h2 style={{ marginBottom: "20px", color: "var(--text)" }}>
              {t('room_reserve_title')}
            </h2>
            <div style={{ marginBottom: "15px" }}>
              <label>{t('room_event_name')}</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="input" style={{ width: "90%" }} />
            </div>
            <div style={{ marginBottom: "15px" }}>
              {/* <label>Start Time:</label> */}
              <CustomDatePicker
                selected={formData.start ? new Date(formData.start) : null}
                onChange={handleChange}
                label={t('modal_start_time')}
                name="start"
                style={{
                  width: "90%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  marginTop: "5px",
                  marginRight: "30px",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              {/* <label>End Time:</label> */}
              <CustomDatePicker
                selected={formData.end ? new Date(formData.end) : null}
                onChange={handleChange}
                label={t('modal_end_time')}
                name="end"
                style={{
                  width: "90%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  marginTop: "5px",
                  marginRight: "30px",
                }}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              {t('room_reserve_button')}
            </button>
            {!showModal && error && (
              <div style={{ color: "var(--color-danger)", textAlign: "center", margin: "10px 0" }}>
                {error}
              </div>
            )}
          </form>
        )}
      </div>

      {/* Right Section */}
          <div style={{ flex: 1, padding: "30px" }}>
        <h2
          style={{ textAlign: "center", color: "var(--text)", marginBottom: "20px" }}
        >
          {t('room_upcoming_events')}
        </h2>
        {upcomingEvents.length > 0 ? (
          upcomingEvents.slice(0, 4).map((event, index) => (
            <div
              key={index}
              style={{
                background: "var(--surface)",
                padding: "15px",
                borderRadius: "10px",
                boxShadow: "var(--shadow-sm)",
                marginBottom: "15px",
              }}
            >
              <p style={{ fontSize: "1.2em" }}>
                <strong>{event.title}</strong> ...
                <span style={{ color: "var(--color-success)" }}>
                  {moment(event.occurrence_start || event.start)
                    .set({
                      year: moment().year(),
                      month: moment().month(),
                      date: moment().date(),
                    })
                    .fromNow()}
                </span>
              </p>
              <p style={{ fontSize: "0.9em" }}>
                <strong>{t('room_start_label')}</strong> {moment(event.occurrence_start || event.start).format("LT")}
              </p>
              <p style={{ fontSize: "0.9em" }}>
                <strong>{t('room_end_label')}</strong> {moment(event.occurrence_end || event.end).format("LT")}
              </p>
              <p style={{ fontSize: "0.9em" }}>
                <strong>{t('room_reserved_by')}</strong> {event.user_name}
              </p>
            </div>
          ))
        ) : (
          <p style={{ textAlign: "center", color: "var(--text-muted)" }}>{t('room_no_upcoming')}</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }} className="modal-overlay">
          <div className="modal-card" style={{ width: 'min(480px, 92vw)' }} dir={currentDir}>
            <h2 style={{ textAlign: "center", color: "var(--text)", marginBottom: "20px" }}>
              {t('room_reserve_after')}
            </h2>
            <form onSubmit={handleReserve}>
              <div style={{ marginBottom: "15px" }}>
                <label>{t('room_event_name')}</label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} className="input" style={{ width: "90%" }} />
              </div>
              <div style={{ marginBottom: "15px" ,}}>
                <CustomDatePicker
                  selected={moment(formData.start).toDate()}
                  onChange={handleChange}
                  label={t('modal_start_time')}
                  name="start"
                  style={{ width: "90%" }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <CustomDatePicker
                  selected={moment(formData.end).toDate()}
                  onChange={handleChange}
                  label={t('modal_end_time')}
                  name="end"
                  style={{ width: "90%" }}
                />
              </div>
              {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "20px",
                }}
              >
                <button type="submit" className="btn btn-primary">{t('room_reserve_button')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setError(false); }}>{t('room_close')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showSuccessModal && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setShowSuccessModal(false); }} className="modal-overlay">
          <div className="modal-card" style={{ width: 'min(420px, 92vw)', textAlign: 'center' }} dir={currentDir}>
            <h2 style={{ color: "var(--color-success)", marginBottom: "10px" }}>{t('room_success_title')}</h2>
            <p style={{ marginBottom: "20px", color: 'var(--text)' }}>{t('room_success_message')}</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowSuccessModal(false)}>{t('room_close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};
export default Room;
